export interface Article {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  categoryName: string;
  author: string;
  date: string;
  readTime: number;
  imageUrl: string;
  featured: boolean;
  tags: string[];
}

export const articles: Article[] = [
  {
    slug: 'hanguo-rujia-lunli-xiandai-chuancheng',
    title: '韩国儒家伦理的现代传承：从朝鲜王朝到当代社会',
    excerpt: '儒家思想自高丽时期传入朝鲜半岛后，经历了朝鲜王朝的全面制度化，成为韩国社会伦理的根基。本文深入探讨儒家伦理在韩国现代化进程中的传承与嬗变。',
    content: `<p>儒家思想自高丽时期（918-1392年）传入朝鲜半岛以来，逐渐渗透到韩国社会的各个层面。朝鲜王朝（1392-1910年）更是将儒家伦理体系全面制度化，使之成为国家治理和社会规范的核心框架。</p>

<h2>朝鲜王朝时期的儒家制度化</h2>

<p>朝鲜王朝以"崇儒抑佛"为国策，建立了以朱子学为中心的国家意识形态。在这一时期，<strong>三纲五常</strong>不仅是个人道德修养的准则，更成为国家法律体系的基础。科举制度以四书五经为核心考试内容，使得儒家伦理通过教育系统深刻影响了整个社会的价值观。</p>

<p>在家庭层面，<strong>宗法制度</strong>被严格执行。家族谱系的维护、祭祀仪式的规范、长幼有序的家庭等级，都体现了儒家伦理在日常生活中的具体化。韩国至今保留的"祭祀"（제사）传统，就是儒家"慎终追远"思想的直接体现。</p>

<h2>日本殖民与现代化冲击</h2>

<p>1910年至1945年的日本殖民统治期间，韩国的儒家传统受到了前所未有的冲击。日本殖民者试图以神道教取代儒家思想，但儒家伦理作为韩国民族认同的核心要素，反而在这一时期得到了强化。</p>

<p>二战后，韩国经历了快速的工业化和现代化。西方个人主义价值观的涌入，对传统的集体主义伦理形成了挑战。然而，<strong>韩国儒家伦理展现出强大的适应性</strong>——它没有被完全取代，而是与现代价值进行了创造性的融合。</p>

<h2>当代韩国社会中的儒家伦理表现</h2>

<p>在当代韩国，儒家伦理的影响仍然无处不在：</p>

<ul>
<li><strong>教育重视：</strong>韩国社会对教育的极度重视，源于儒家"学而优则仕"的传统观念。</li>
<li><strong>人际关系网络：</strong>"血缘、地缘、学缘"三缘关系在韩国社会中仍然发挥着重要作用。</li>
<li><strong>长幼有序：</strong>韩国语中的敬语体系、餐桌礼仪中的尊卑次序，都是儒家伦理在日常生活中的具体表达。</li>
<li><strong>集体主义取向：</strong>韩国人在面对个人与集体利益冲突时，仍然倾向于以集体利益为重。</li>
</ul>

<h2>传统与现代的创造性张力</h2>

<p>韩国儒家伦理在现代社会中并非一成不变地延续，而是在与民主、平等、人权等现代价值观的互动中不断调适。它既不是简单的延续，也不是彻底的断裂，而是一种<strong>创造性的转化</strong>。</p>`,
    category: 'rujia-lunli-chuantong',
    categoryName: '儒家伦理传统',
    author: '金志勋',
    date: '2025-12-15',
    readTime: 12,
    imageUrl: 'https://readdy.ai/api/search-image?query=Ancient%20Korean%20Confucian%20academy%20with%20scholars%20in%20traditional%20white%20hanbok%20discussing%20classical%20texts%20under%20ancient%20pine%20trees%2C%20serene%20mountain%20setting%2C%20warm%20afternoon%20sunlight%2C%20traditional%20Korean%20architecture%20with%20curved%20roofs%2C%20cultural%20and%20scholarly%20atmosphere&width=600&height=450&seq=article-img-0&orientation=landscape',
    featured: true,
    tags: ['儒家伦理', '传统文化', '韩国历史', '朝鲜王朝'],
  },
  {
    slug: 'hanguo-xiaodao-wenhua-jiazuguan',
    title: '孝道文化的当代诠释：韩国家庭伦理的核心支柱',
    excerpt: '孝道是韩国儒家伦理体系中最核心的概念之一。本文从历史演变、当代实践和代际关系三个维度，全面解析韩国孝道文化的深层结构。',
    content: `<p>在韩国伦理文化中，<strong>孝道（효도）</strong>占据着不可撼动的核心地位。它不仅是家庭伦理的第一要义，更是整个社会伦理秩序的基石。</p>

<h2>孝道的历史根源</h2>

<p>韩国的孝道观念源远流长。早在三国时期（公元前57年-公元668年），孝行故事就已经成为道德教化的重要内容。朝鲜王朝时期，统治者将《孝经》列为国学必修。</p>

<p>在传统观念中，孝道包含三个层次：<strong>赡养父母（物质层面）、尊敬父母（精神层面）、光宗耀祖（社会层面）</strong>。</p>

<h2>当代韩国孝道的实践形态</h2>

<p>在现代韩国，孝道的实践形式发生了显著变化，但其精神内核仍然延续：</p>

<ul>
<li><strong>经济支持：</strong>调查显示，超过60%的韩国成年子女定期向父母提供经济支持。</li>
<li><strong>节日团聚：</strong>每逢重要节日，韩国人会不远千里回到故乡与父母团聚。</li>
<li><strong>医疗照护：</strong>韩国子女仍然将亲自照顾年迈父母视为不可推卸的责任。</li>
<li><strong>祭祀仪式：</strong>为已故祖先举行的祭祀仪式在大多数韩国家庭中仍然被严格遵守。</li>
</ul>

<h2>代际关系的现代张力</h2>

<p>年轻一代韩国人正在探索一种<strong>"新型孝道"</strong>——既不放弃对父母的关爱，又不完全牺牲个人的人生选择。这种新型孝道更强调情感交流和精神陪伴，而非单纯的经济支持和形式化的仪式。</p>`,
    category: 'jiating-shehui-lunli',
    categoryName: '家庭与社会伦理',
    author: '朴恩珠',
    date: '2025-11-28',
    readTime: 10,
    imageUrl: 'https://readdy.ai/api/search-image?query=Tender%20moment%20between%20Korean%20elderly%20mother%20and%20adult%20daughter%20sharing%20tea%20in%20a%20traditional%20hanok%20home%2C%20warm%20natural%20light%20filtering%20through%20rice%20paper%20doors%2C%20genuine%20emotional%20connection%2C%20cultural%20warmth%20and%20respect%2C%20editorial%20portrait%20photography&width=600&height=450&seq=article-img-1&orientation=landscape',
    featured: true,
    tags: ['孝道文化', '家庭伦理', '代际关系', '传统文化'],
  },
  {
    slug: 'hanguo-zhichang-shangxiaji-guanxi',
    title: '韩国职场中的上下级关系：等级伦理与组织效能',
    excerpt: '韩国企业的等级制度举世闻名。本文分析韩国职场中独特的上下级关系伦理，探讨其文化根源、现实表现以及在全球竞争中的调适策略。',
    content: `<p>韩国职场文化中的<strong>上下级关系（상하관계）</strong>是全球商业文化研究中的经典课题。从"会食"到"报告体系"，韩国企业的等级伦理渗透到工作的每一个细节。</p>

<h2>等级伦理的文化根源</h2>

<p>韩国职场中的等级观念，深深植根于儒家伦理中的<strong>"长幼有序"</strong>原则和军事化的国家管理传统。在语言层面，韩语的敬语体系本身就是等级伦理的语言载体。</p>

<h2>等级伦理的具体表现</h2>
<ul>
<li><strong>决策模式：</strong>韩国企业的决策往往采用自上而下的方式。</li>
<li><strong>会食文化：</strong>公司聚餐不是简单的社交活动，而是一种仪式化的等级确认场合。</li>
<li><strong>年龄与职级：</strong>在韩国职场中，年龄往往比职级更受重视。</li>
<li><strong>面子文化：</strong>公开批评上级或同事被视为严重冒犯。</li>
</ul>

<h2>全球化背景下的调适</h2>
<p>随着韩国企业的全球化和新一代员工的价值观变化，传统的等级伦理正在经历深刻的调整：扁平化改革、英语名文化、绩效导向、工作生活平衡等趋势正在改变韩国职场。</p>`,
    category: 'zhichang-lunli-guifan',
    categoryName: '职场伦理规范',
    author: '李俊昊',
    date: '2025-11-10',
    readTime: 11,
    imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20Korean%20corporate%20office%20meeting%20scene%20with%20senior%20and%20junior%20employees%20in%20respectful%20interaction%2C%20clean%20glass%20walled%20conference%20room%2C%20professional%20attire%2C%20warm%20evening%20light%20through%20windows%2C%20hierarchical%20yet%20harmonious%20corporate%20culture&width=600&height=450&seq=article-img-2&orientation=landscape',
    featured: false,
    tags: ['职场伦理', '等级文化', '企业管理', '韩国职场'],
  },
  {
    slug: 'jishengchong-yu-hanguo-lunli-kunjing',
    title: '《寄生虫》与韩国社会伦理困境：阶级、人性与道德边界',
    excerpt: '奉俊昊导演的《寄生虫》斩获奥斯卡最佳影片，这部作品不仅是电影艺术的巅峰，更是一面映照韩国社会伦理深层矛盾的多棱镜。',
    content: `<p>2019年，奉俊昊导演的<strong>《寄生虫》（기생충）</strong>创造了历史，成为首部获得奥斯卡最佳影片的非英语电影。</p>

<h2>空间隐喻中的伦理等级</h2>
<p>电影中精心设计的空间结构——<strong>半地下室、豪宅、地下防空洞</strong>——构成了一个视觉化的伦理等级体系。这种空间配置深刻地反映了韩国社会的<strong>"层"（계층）</strong>意识。</p>

<h2>生存伦理与道德边界的崩塌</h2>
<p>金家四口人通过欺骗手段逐一进入朴家工作的过程，呈现了一个层层递进的道德滑坡。<strong>奉俊昊并非简单地谴责金家的行为</strong>，而是引导观众思考在极度不平等的结构中，"道德"本身是否已经变成了一种奢侈品。</p>

<h2>"气味"：不可逾越的伦理鸿沟</h2>
<p>电影中最精妙的伦理隐喻之一是贯穿始终的<strong>"气味"</strong>意象。这个隐喻直指韩国社会的核心困境：<strong>形式上的平等与实质上的不平等之间的巨大裂隙</strong>。</p>

<h2>韩国影视中的伦理叙事传统</h2>
<p>从李沧东的《燃烧》到黄东赫的《鱿鱼游戏》，韩国影视作品中对社会伦理问题的关注构成了一个连续的叙事传统，为理解当代韩国提供了独特的视觉入口。</p>`,
    category: 'yingshi-wenhua-lunli',
    categoryName: '影视文化中的伦理',
    author: '崔智友',
    date: '2025-10-22',
    readTime: 14,
    imageUrl: 'https://readdy.ai/api/search-image?query=Cinematic%20film%20still%20aesthetic%20inspired%20by%20Korean%20cinema%2C%20dramatic%20contrast%20between%20luxurious%20modern%20interior%20and%20dark%20underground%20spaces%2C%20warm%20and%20cold%20color%20contrast%2C%20social%20class%20symbolism%2C%20artistic%20composition%20with%20deep%20emotional%20resonance&width=600&height=450&seq=article-img-3&orientation=landscape',
    featured: true,
    tags: ['韩国电影', '社会伦理', '阶级分析', '影视文化'],
  },
  {
    slug: 'hanguo-gerenzhuyi-jitizhuyi-bolang',
    title: '个人主义与集体主义的博弈：韩国现代社会伦理的动态平衡',
    excerpt: '韩国社会正经历从传统集体主义向个体化社会的深刻转型。本文分析这两种伦理取向的博弈如何塑造了当代韩国人的价值观念与行为模式。',
    content: `<p>理解韩国现代社会伦理的关键，在于把握<strong>个人主义与集体主义</strong>之间持续而复杂的动态博弈。</p>

<h2>集体主义的传统根基</h2>
<p>韩国的集体主义传统有着深厚的历史基础。在农业社会中，<strong>村落共同体</strong>是基本的社会单位。儒家伦理进一步强化了这种集体取向。</p>

<h2>个体化浪潮的兴起</h2>
<p>1997年亚洲金融危机成为韩国社会价值观转型的分水岭。"N抛世代"现象、"独自经济"的兴起、"小确幸"的追求，都标志着个体意识的觉醒。</p>

<h2>新型集体主义的探索</h2>
<p>韩国社会并非简单地走向原子化个人主义。兴趣社群、社会运动、数字共同体等新的集体形式正在出现，展现了个人与集体之间动态平衡的新可能。</p>`,
    category: 'xiandai-shehui-lunli',
    categoryName: '现代社会伦理观',
    author: '郑秀妍',
    date: '2025-10-05',
    readTime: 13,
    imageUrl: 'https://readdy.ai/api/search-image?query=Diverse%20group%20of%20young%20Koreans%20in%20Seoul%2C%20some%20engaged%20in%20group%20activities%2C%20others%20enjoying%20solitude%2C%20modern%20urban%20setting%20with%20neon%20signs%20and%20traditional%20elements%2C%20warm%20golden%20hour%20lighting&width=600&height=450&seq=article-img-4&orientation=landscape',
    featured: true,
    tags: ['个人主义', '集体主义', '社会转型', '现代伦理'],
  },
  {
    slug: 'hanguo-jiaoyu-lunli-yure',
    title: '教育至上：韩国教育伦理的狂热、成就与代价',
    excerpt: '韩国是全球教育竞争最激烈的国家之一。本文剖析韩国教育狂热背后的伦理逻辑，探讨这种教育伦理如何塑造了韩国的成就与困境。',
    content: `<p>韩国的<strong>教育狂热（교육열）</strong>是全球教育学研究中的经典案例。从清晨的补习班到深夜的自习室，韩国学生的时间表折射出一种独特的教育伦理。</p>

<h2>教育伦理的历史建构</h2>
<p>韩国教育伦理的形成与科举制度传统密切相关。韩国战争后的极度贫困，进一步强化了教育的工具价值——<strong>教育是唯一可以"投资"的未来</strong>。</p>

<h2>教育伦理的正面效应与暗面</h2>
<p>韩国教育培养出的勤奋和专注力被全球企业广泛认可，但青少年心理健康危机、"SKY"名校执念、教育不平等加剧等问题也日益严重。韩国社会正开始反思这种极端的教育伦理。</p>`,
    category: 'xiandai-shehui-lunli',
    categoryName: '现代社会伦理观',
    author: '金志勋',
    date: '2025-09-18',
    readTime: 11,
    imageUrl: 'https://readdy.ai/api/search-image?query=Korean%20students%20studying%20diligently%20in%20a%20modern%20library%20filled%20with%20warm%20lighting%2C%20books%20stacked%20on%20desks%2C%20focused%20expressions%2C%20educational%20environment%20with%20traditional%20Korean%20design%20elements&width=600&height=450&seq=article-img-5&orientation=landscape',
    featured: false,
    tags: ['教育伦理', '社会竞争', '青少年', '教育改革'],
  },
  {
    slug: 'hanguo-jisi-wenhua-lunli-neihan',
    title: '祭祀文化与慎终追远：韩国祖先崇拜的伦理内涵',
    excerpt: '韩国祭祀文化是儒家"慎终追远"思想最生动的实践。本文解读韩国祭祀仪式的各个环节，揭示其背后深厚的伦理意涵与当代社会意义。',
    content: `<p>在韩国，<strong>祭祀（제사）</strong>不仅是一种宗教或民俗仪式，更是一套完整的伦理实践体系。</p>

<h2>祭祀的类型与结构</h2>
<p>韩国的祖先祭祀体系复杂而精密：<strong>忌祭祀</strong>在祖先忌日举行；<strong>节日祭祀</strong>在春节和秋夕等重大节日举行。祭祀的每一个环节都有严格的规范。</p>

<h2>祭祀的伦理维度</h2>
<p>祭祀将孝道从生前延伸到死后，构建了一个超越生死的伦理责任链条。通过共同参与祭祀，家族成员确认并强化了彼此之间的血缘纽带和等级关系。</p>

<h2>现代化进程中的祭祀变迁</h2>
<p>近年来，韩国的祭祀文化在经历显著变化。但无论形式如何变化，<strong>祭祀所承载的"追念先人、凝聚家族"的伦理内核</strong>仍然保持着强大的生命力。</p>`,
    category: 'jiating-shehui-lunli',
    categoryName: '家庭与社会伦理',
    author: '朴恩珠',
    date: '2025-09-02',
    readTime: 10,
    imageUrl: 'https://readdy.ai/api/search-image?query=Traditional%20Korean%20ancestral%20ritual%20table%20with%20carefully%20arranged%20offerings%20of%20food%20and%20fruit%2C%20incense%20burning%20gently%2C%20warm%20candlelight%2C%20traditional%20hanok%20room%20setting%2C%20solemn%20and%20respectful%20atmosphere&width=600&height=450&seq=article-img-6&orientation=landscape',
    featured: false,
    tags: ['祭祀文化', '祖先崇拜', '慎终追远', '传统文化'],
  },
  {
    slug: 'hanguo-yueli-tiyan-jingzheng',
    title: '韩国约会伦理：从相亲到恋爱的新旧文化碰撞',
    excerpt: '韩国年轻人的约会文化正经历前所未有的变革。本文剖析传统相亲、现代约会APP与"速食爱情"之间的伦理张力。',
    content: `<p>韩国的<strong>约会文化（연애문화）</strong>是一个充满张力的领域——传统与现代、东方与西方、保守与开放的价值观在这里激烈碰撞。</p>

<h2>传统相亲：家庭主导的婚配模式</h2>
<p>在传统韩国社会，<strong>相亲（선/맞선）</strong>是通往婚姻的主要途径。<strong>婚姻不仅仅是两个人的结合，更是两个家族的联盟</strong>。</p>

<h2>约会APP与新型亲密关系</h2>
<p>随着智能手机的普及，韩国年轻人的约会方式发生了革命性变化。数字约会也带来了信息真实性、隐私保护等新的伦理问题。</p>

<h2>"速食爱情"与长期承诺之间的张力</h2>
<p>韩国年轻人面临的核心伦理困境是<strong>"速食爱情"</strong>与社会对稳定关系的期待之间的矛盾。</p>`,
    category: 'xiandai-shehui-lunli',
    categoryName: '现代社会伦理观',
    author: '郑秀妍',
    date: '2025-08-14',
    readTime: 9,
    imageUrl: 'https://readdy.ai/api/search-image?query=Young%20Korean%20couple%20having%20a%20meaningful%20conversation%20at%20a%20trendy%20Seoul%20cafe%20with%20warm%20ambient%20lighting%2C%20modern%20minimalist%20interior%2C%20emotional%20connection%20visible%20in%20their%20expressions&width=600&height=450&seq=article-img-7&orientation=landscape',
    featured: false,
    tags: ['约会文化', '亲密关系', '代际差异', '社会变迁'],
  },
  {
    slug: 'youyu-youxi-hanguo-lunli-jingxiang',
    title: '《鱿鱼游戏》：生存游戏背后的伦理启示录',
    excerpt: 'Netflix现象级剧集《鱿鱼游戏》不仅是一部惊悚剧，更是对当代韩国社会伦理困境的极端化隐喻。',
    content: `<p>2021年，<strong>《鱿鱼游戏》（오징어 게임）</strong>成为Netflix有史以来观看量最高的剧集，是当代韩国社会伦理危机的一则残酷寓言。</p>

<h2>债务经济与人性异化</h2>
<p>剧中456名参赛者都是被<strong>债务</strong>逼到绝境的边缘人。游戏中，456亿韩元的奖金总额正好映射了参赛者的数量——<strong>每一条人命都被赋予了一个精确的价码</strong>。</p>

<h2>平等的神话与现实的残酷</h2>
<p>游戏组织者反复强调<strong>"公平"（공정）</strong>是最高原则。然而，这种形式上的平等恰恰掩盖了更深层的不平等。</p>`,
    category: 'yingshi-wenhua-lunli',
    categoryName: '影视文化中的伦理',
    author: '崔智友',
    date: '2025-08-01',
    readTime: 12,
    imageUrl: 'https://readdy.ai/api/search-image?query=Dramatic%20cinematic%20aesthetic%20inspired%20by%20Korean%20thriller%20drama%2C%20stark%20contrast%20between%20colorful%20playground-like%20setting%20and%20dark%20undertones%2C%20surreal%20stairs%20and%20geometric%20architecture%2C%20warm%20pastel%20colors&width=600&height=450&seq=article-img-8&orientation=landscape',
    featured: false,
    tags: ['韩国影视', '社会伦理', '阶级批判', '全球化'],
  },
  {
    slug: 'hanguo-changyou-zunbei-zhixu',
    title: '长幼有序：韩国语敬语体系中的伦理秩序',
    excerpt: '韩语是世界上敬语系统最复杂的语言之一。本文分析韩国语敬语体系如何编码和再生产社会伦理秩序。',
    content: `<p>在世界上6000多种语言中，韩语的<strong>敬语体系（존댓말 체계）</strong>堪称最为精妙复杂的之一。韩语敬语不仅是语法现象，更是韩国伦理文化的语言编码。</p>

<h2>敬语的六个等级</h2>
<p>现代韩语的敬语体系包含六个基本语阶。使用错误的语阶不仅是不礼貌的表现，在特定情境下甚至可能构成社交冒犯。</p>

<h2>敬语中的伦理编码</h2>
<p>韩语敬语体系编码了年龄伦理、地位伦理、关系伦理、家庭伦理等多重伦理维度。在当代韩国，敬语体系也面临着界限模糊、职场英语名现象等挑战。</p>`,
    category: 'xiandai-shehui-lunli',
    categoryName: '现代社会伦理观',
    author: '金志勋',
    date: '2025-07-20',
    readTime: 8,
    imageUrl: 'https://readdy.ai/api/search-image?query=Close%20up%20of%20traditional%20Korean%20calligraphy%20brush%20writing%20Hangul%20characters%20on%20rice%20paper%2C%20elegant%20hand%20movements%2C%20warm%20natural%20lighting%2C%20ink%20stone%20and%20brush%20rest%20visible%2C%20scholarly%20and%20refined%20atmosphere&width=600&height=450&seq=article-img-9&orientation=landscape',
    featured: false,
    tags: ['韩语敬语', '语言伦理', '社会等级', '文化语言学'],
  },
  {
    slug: 'hanguo-meinv-jingji-lunli-zhengli',
    title: '韩国"美貌经济"的伦理争议：整容文化与身体自主',
    excerpt: '韩国是全球整容率最高的国家。本文从伦理视角审视韩国的美貌经济现象，探讨社会压力、个人选择与审美标准化之间的复杂关系。',
    content: `<p>韩国被称为<strong>"整容共和国"</strong>并非夸张。据统计，韩国每1000人中进行的整容手术数量位居全球第一。</p>

<h2>美貌作为一种"社会资本"</h2>
<p>在韩国社会中，外貌不仅仅是个人审美偏好的问题，更是一种具有实际交换价值的社会资本。这种社会环境创造了一种强大的规范性压力。</p>

<h2>个人选择还是社会强制？</h2>
<p>韩国整容文化引发的核心伦理争议在于：整容到底是个人的自由选择，还是社会压力的无奈顺从？当审美标准单一化时，"自愿"本身就值得怀疑。</p>`,
    category: 'xiandai-shehui-lunli',
    categoryName: '现代社会伦理观',
    author: '郑秀妍',
    date: '2025-07-05',
    readTime: 10,
    imageUrl: 'https://readdy.ai/api/search-image?query=Reflective%20Korean%20woman%20looking%20at%20herself%20in%20a%20mirror%20in%20a%20modern%20minimalist%20beauty%20space%2C%20soft%20natural%20lighting%20creating%20gentle%20shadows%2C%20thoughtful%20expression%2C%20clean%20aesthetic%20with%20warm%20neutral%20tones&width=600&height=450&seq=article-img-10&orientation=landscape',
    featured: false,
    tags: ['美貌经济', '整容文化', '身体伦理', '社会压力'],
  },
  {
    slug: 'hanguo-junshi-fuyi-lunli',
    title: '兵役与公民伦理：韩国男性兵役制度的社会伦理分析',
    excerpt: '韩国是全球少数实行全民男性义务兵役制的国家之一。本文分析兵役制度在韩国社会伦理体系中的位置与引发的争议。',
    content: `<p>在韩国，<strong>义务兵役制（병역의무）</strong>是所有20至28岁男性公民必须履行的法律义务，更是韩国社会伦理体系中的一个关键节点。</p>

<h2>兵役作为"公民成年礼"</h2>
<p>在韩国社会文化中，兵役被建构为一种<strong>"公民成年礼"</strong>——完成兵役的男性才被视为"真正的成年人"。</p>

<h2>兵役制度引发的伦理争议</h2>
<p>韩国的兵役制度也引发了多重伦理争议：体育明星和艺术家的豁免特权、性别平等问题、良心拒服兵役者的权利、兵役对男性职业发展的影响等。</p>`,
    category: 'rujia-lunli-chuantong',
    categoryName: '儒家伦理传统',
    author: '李俊昊',
    date: '2025-06-18',
    readTime: 9,
    imageUrl: 'https://readdy.ai/api/search-image?query=Korean%20soldiers%20in%20ceremonial%20uniform%20standing%20in%20respectful%20formation%20at%20a%20traditional%20Korean%20palace%20grounds%2C%20warm%20sunlight%20creating%20long%20shadows%2C%20ancient%20architecture%20blending%20with%20modern%20military%20duty&width=600&height=450&seq=article-img-11&orientation=landscape',
    featured: false,
    tags: ['兵役制度', '公民伦理', '性别平等', '社会责任'],
  },
];

export const featuredArticles = articles.filter((a) => a.featured);
export const latestArticles = [...articles].sort(
  (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
);

export function getArticlesByCategory(slug: string): Article[] {
  return articles.filter((a) => a.category === slug);
}

export function getArticleBySlug(slug: string): Article | undefined {
  return articles.find((a) => a.slug === slug);
}

export function getRelatedArticles(article: Article, count: number = 3): Article[] {
  return articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, count);
}